/// <reference types="vite/client" />

import type { ReactNode } from "react";
import {
  createRootRoute,
  HeadContent,
  Scripts,
  useParams,
  notFound,
  Outlet,
} from "@tanstack/react-router";
import { publicEnv } from "../utils/env";
import { getProjectFromSubdomain } from "../utils/subdomain";
import { lazy } from "react";
import { z } from "zod";
import { RenderProvider, ThemeProvider } from "@op/utils";
import { LangProvider } from "../providers/LangProvider";
import { loadCatalog } from "../i18n";
// import { loadCss } from "../utils/css";
import { createServerFn } from "@tanstack/react-start";
import { rendererClient } from "../lib/rendererClient";
import type { LanguageKey } from "@op/puck-types";
import { loadCss } from "../utils/css";

loadCatalog("en");

const TanStackRouterDevtoolsPanel = lazy(() =>
  import("@tanstack/react-router-devtools").then((mod) => ({
    default: mod.TanStackRouterDevtoolsPanel,
  })),
);

const TanStackDevtools = lazy(() =>
  import("@tanstack/react-devtools").then((mod) => ({
    default: mod.TanStackDevtools,
  })),
);

const searchSchema = z.object({
  print: z.boolean().optional(),
});

const getLanguages = createServerFn({
  method: "GET",
})
  .inputValidator(
    z.object({ organizationSlug: z.string(), projectSlug: z.string() }),
  )
  .handler(async ({ data: { organizationSlug, projectSlug } }) => {
    const res = await rendererClient.projects[":organizationSlug"][
      ":projectSlug"
    ].languages.$get({
      param: {
        organizationSlug,
        projectSlug,
      },
    });
    if (!res.ok) {
      if (res.status === 404) throw notFound();
      throw new Error("Failed to fetch project languages");
    }
    const languages = await res.json();
    return languages.map((language) => language.key) as LanguageKey[];
  });

export const Route = createRootRoute({
  validateSearch: searchSchema,
  beforeLoad: async ({ search }) => {
    const { organizationSlug, projectSlug } = await getProjectFromSubdomain();
    const languages = await getLanguages({
      data: {
        organizationSlug,
        projectSlug,
      },
    });
    const renderMedium = search.print ? "print" : "screen";
    return {
      organizationSlug,
      projectSlug,
      renderMedium,
      languages,
    };
  },
  loader: ({ context: { organizationSlug, projectSlug, renderMedium } }) => {
    return { cssLinks: loadCss(organizationSlug, projectSlug, renderMedium) };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { cssLinks } = loaderData;
    return {
      meta: [
        {
          charSet: "utf-8",
        },
        {
          name: "viewport",
          content: "width=device-width, initial-scale=1",
        },
      ],
      links: cssLinks,
    };
  },
  shellComponent: RootLayout,
});

function RootLayout() {
  const { languageKey } = useParams({ strict: false });
  const { organizationSlug, projectSlug, renderMedium } =
    Route.useRouteContext();

  return (
    <RootDocument lang={(languageKey as any) ?? "en"}>
      <LangProvider languageKey={(languageKey as any) ?? "en"}>
        <ThemeProvider
          as="body"
          organizationSlug={organizationSlug}
          projectSlug={projectSlug}
          defaultOrganizationSlug={publicEnv.VITE_DEFAULT_ORGANIZATION_SLUG}
          defaultProjectSlug={publicEnv.VITE_DEFAULT_PROJECT_SLUG}
          forceMode={renderMedium === "print" ? "light" : undefined}
        >
          <RenderProvider
            value={{
              context: "renderer",
              renderMedium: renderMedium as "print" | "screen",
              apiUrl: publicEnv.VITE_API_URL,
            }}
          >
            <Outlet />
          </RenderProvider>
          {import.meta.env.DEV && (
            <TanStackDevtools
              config={{
                position: "bottom-left",
              }}
              plugins={[
                {
                  name: "Tanstack Router",
                  render: <TanStackRouterDevtoolsPanel />,
                },
              ]}
            />
          )}
        </ThemeProvider>
      </LangProvider>
    </RootDocument>
  );
}

function RootDocument({
  children,
  lang,
}: Readonly<{ children: ReactNode; lang?: string }>) {
  return (
    <html lang={lang}>
      <head>
        <HeadContent />
      </head>
      {children}
      <Scripts />
    </html>
  );
}
